import { useState, useEffect, useCallback } from 'react';
import { settingsApi } from '../../services/api/endpoints/settings';
import type { AccountingLevelSettings, AccountingUnit } from '../../types/accounting-levels';

const STORAGE_KEY = 'accounting-level-settings';

const DEFAULT_SETTINGS: AccountingLevelSettings = {
  type: 'single',
  consolidation: false,
  mainCurrency: 'CDF',
  units: [],
  consolidationRules: {
    eliminateIntercompany: true,
    convertCurrency: true,
    adjustments: true
  }
};

function loadFromStorage(): AccountingLevelSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function useAccountingLevels() {
  const [settings, setSettings] = useState<AccountingLevelSettings>(loadFromStorage);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger depuis l'API au montage
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await settingsApi.getSettings();
        if (response.success && response.data) {
          const levelSettings = (response.data as any)?.accounting?.accountingLevel;
          if (levelSettings) {
            const merged = { ...DEFAULT_SETTINGS, ...levelSettings };
            setSettings(merged);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          }
        }
      } catch {
        // Silencieux — on garde l'état chargé depuis localStorage
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const addUnit = (unit: Omit<AccountingUnit, 'id' | 'currency' | 'isConsolidated'>) => {
    setSettings(prev => ({
      ...prev,
      units: [...prev.units, {
        id: crypto.randomUUID(),
        ...unit,
        currency: settings.mainCurrency,
        isConsolidated: true
      }]
    }));
  };

  const removeUnit = (id: string) => {
    setSettings(prev => ({
      ...prev,
      units: prev.units.filter(unit => unit.id !== id)
    }));
  };

  const updateSettings = (updates: Partial<AccountingLevelSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const saveSettings = useCallback(async (): Promise<boolean> => {
    try {
      setSaving(true);
      setError(null);
      await settingsApi.updateSettings({
        category: 'accounting',
        settings: { accountingLevel: settings } as any
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      return true;
    } catch (err) {
      console.error('Erreur sauvegarde niveaux comptables:', err);
      // Fallback : persister au moins en localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setError('Sauvegarde locale uniquement (API indisponible)');
      return false;
    } finally {
      setSaving(false);
    }
  }, [settings]);

  return {
    settings,
    loading,
    saving,
    error,
    addUnit,
    removeUnit,
    updateSettings,
    saveSettings
  };
}